import { Injectable } from '@angular/core';
import { catchError, concatMap, defer, EMPTY, expand, from, last, Observable, of, tap, throwError, zip } from 'rxjs';
import { filter, map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { FileUtilsService } from './file-utils.service';
import { environment } from '../../../environments/environment';
import { FileService } from '../api/file.service';
import { SubTaskInProgress, TaskInProgress } from '../models/task-in-progress';
import {
  ModelDownloadTaskInProgress,
  ModelDownloadTaskInProgressService
} from './model-download-task-in-progress.service';

/** Typed error thrown when the blob-storage SAS URL returns a non-2xx HTTP status. */
export class HttpDownloadError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'HttpDownloadError';
  }
}

export class FileSaveRequest {
  id: string;
  filePath: string;
  fileName: string;
  blobUrl: string;
  fileSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class FilesWriterService {
  // this is the directory handler list in ONE uploading session, need to clean every time after uploading
  private dirHandlerMap = new Map<string, any>();

  public constructor(private fileUtilsService: FileUtilsService, private fileService: FileService) { }

  // clean the dir handler map for the next uploading session
  public cleanDirHandlerMap() {
    this.dirHandlerMap.clear();
  }

  public getDirectoryHandle(): Observable<any> {
    return defer(() => (window as any).showDirectoryPicker());
  }

  public requestPermission(dirHandle: any, permissionOptions: { mode: string }): Observable<any> {
    return defer(() => dirHandle.requestPermission(permissionOptions));
  }

  public getDirectoryHandleWithPermission(permissionOptions: { mode: string }): Observable<any> {
    return this.getDirectoryHandle().pipe(
      switchMap(p => this.requestPermission(p, permissionOptions).pipe(map(_ => p)))
    );
  }

  downloadBlobsForFileAndWriteToStream(
    writeableStream: any,
    file: FileSaveRequest,
    progressCallBack: (subTaskInProgress: ModelDownloadTaskInProgress) => void
  ): Observable<boolean> {
    let totalSize = 0;
    const defaultWriter = writeableStream.getWriter();
    return defer(() => defaultWriter.ready).pipe(
      switchMap(_ => {
        const sizesToBeRead = this.fileUtilsService.getSizesToBeReadInChunks(
          file.fileSize,
          environment.maxFileUploadChunkSize
        );
        return from(sizesToBeRead.length === 0 ? [[0, 1]] : sizesToBeRead).pipe(
          concatMap(([startRead, endRead]) => {
            return this.fileService.getChunk(file.blobUrl, startRead, endRead - 1).pipe(
              switchMap(res => {
                if (!res.ok) {
                  throw new HttpDownloadError(res.status, `HTTP ${res.status}`);
                }
                const reader = res.body.getReader();
                return defer(() => reader.read()).pipe(
                  expand(project => {
                    if (project.value) {
                      defaultWriter.write(project.value);
                    }
                    if (project.done) {
                      reader.cancel();
                      return EMPTY;
                    }
                    return defer(() => reader.read());
                  })
                );
              }),
              tap(res => {
                if (res?.value) {
                  totalSize += res.value.byteLength;
                }
                progressCallBack({
                  Id: file.id,
                  Progress: (endRead / file.fileSize) * 100,
                  Status: ModelDownloadTaskInProgressService.TaskInProgressStatus
                } as ModelDownloadTaskInProgress);
              })
            );
          }),
          // map(res => {
          //   // const reader = res.body.getReader();
          //   // defaultWriter.write(reader);
          //   return true;
          // }),
          last(),
          switchMap(_ => {
            return defer(() => defaultWriter.ready).pipe(map(_ => true));
          }),
          switchMap(_ => {
            if (file.fileSize > 0 && totalSize !== file.fileSize) {
              return throwError(new Error(`Content-length mismatch: expected ${file.fileSize}, got ${totalSize}`));
            }
            return of(true);
          })
        );
      }),
      tap(_ => {
        //defaultWriter.close();
        defaultWriter.releaseLock();
        writeableStream.close();
        progressCallBack({
          Id: file.id,
          Progress: 100,
          Status: ModelDownloadTaskInProgressService.TaskSuccessStatus
        } as ModelDownloadTaskInProgress);
      }),
      catchError(err => {
        const reason: string = err?.message || 'Download failed';
        progressCallBack({
          Id: file.id,
          Progress: 100,
          Status: ModelDownloadTaskInProgressService.TaskFailureStatus,
          ErrorReason: reason
        } as ModelDownloadTaskInProgress);
        //defaultWriter.close();
        defaultWriter.releaseLock();
        writeableStream.close();
        console.error('downloadModelFile', file, err);
        return throwError(err);
      })
    );
  }

  

  writeFiles(
    rootDirHandle: any,
    files: FileSaveRequest[],
    progressCallBack: (subTaskInProgress: ModelDownloadTaskInProgress) => void
  ): Observable<FileSaveRequest[]> {
    const rootDirNames = [...new Set(files.map(p => p.filePath.split('/')[0]))];
    this.cleanDirHandlerMap();
    return from(rootDirNames).pipe(
      mergeMap(rootDirName => {
        const filesInRootFolder = files.filter(p => p.filePath.split('/')[0] === rootDirName);
        if (rootDirName === '') rootDirName = uuidv4();
        return this.writeForRootFolder(rootDirHandle, filesInRootFolder, rootDirName, progressCallBack);
      }, 2),
      toArray(),
      map(arrFileSaveRequests => arrFileSaveRequests.flat())
    );
  }

  writeForRootFolder(
    rootParentDirHandle: any,
    filesInRootFolder: FileSaveRequest[],
    rootDirName: string,
    progressCallBack: (subTaskInProgress: ModelDownloadTaskInProgress) => void
  ): Observable<FileSaveRequest[]> {
    return this.recursivelyCreateOrGetFolder(rootParentDirHandle, rootDirName, true).pipe(
      switchMap(parentHandle =>
        from(filesInRootFolder.map(p => [p, parentHandle])).pipe(
          mergeMap(([fileInRootFolder, parentHandle]) => {
            const remainingPath = fileInRootFolder.filePath.split('/');
            remainingPath.shift(); //remove the folder wrt the parentHandle
            return this.getDirHandleForFilePath(parentHandle, remainingPath.join('/')).pipe(
              tap(dirHandle => {
                // search by key, if not exists save the dirHandle for the path
                let key = "";
                if(remainingPath.length > 0) key = parentHandle.name + '/' + remainingPath.join('/');
                else key = parentHandle.name;
                let value = this.dirHandlerMap.get(key);
                if(!value) {
                  this.dirHandlerMap.set(key, dirHandle);
                }
              }),
              switchMap((dirHandle: any) => this.getWriteableStream(dirHandle, fileInRootFolder.fileName)),
              switchMap((writeableStream: any) =>
                this.downloadBlobsForFileAndWriteToStream(writeableStream, fileInRootFolder, progressCallBack).pipe(
                  map(_ => fileInRootFolder),
                  catchError(err => {
                    console.error('error downloading blobs and writing a file', fileInRootFolder, filesInRootFolder, err);
                    throw err;
                  })
                )
              ),
              catchError(err => {
                console.error('error writing a file within folder', fileInRootFolder, filesInRootFolder, err);
                progressCallBack({
                  Id: fileInRootFolder.id,
                  Progress: 0,
                  Status: ModelDownloadTaskInProgressService.TaskFailureStatus
                } as ModelDownloadTaskInProgress);
                return of(false);
              })
            );
          }, environment.maxFileUploadsConcurrently),
          catchError(err => {
            console.error('error writing files in folder', filesInRootFolder, err);
            return of(null);
          }),
          filter(p => !!p),
          toArray()
        )
      )
    );
  }

  
  public getWriteableStream(dirHandle: any, fileName: string): Observable<any> {
    return defer(() => dirHandle.getFileHandle(fileName, { create: true })).pipe(
      switchMap((fileHandle: any) => {
        return defer(() => fileHandle.createWritable());
      })
    );
  }

  public getDirHandleForFilePath(fromDirHandle: any, filePath: string): Observable<any> {
    if (!filePath) return of(fromDirHandle);
    const parts = filePath.split('/');
    let index = 0;
    // check if exists dirhandler, get it
    let existPathHandler = this.dirHandlerMap.get(fromDirHandle.name + '/' + filePath);
    if(existPathHandler) return of(existPathHandler);
    //otherwise, create it
    return this.recursivelyCreateOrGetFolder(fromDirHandle, parts[index++], false)
      .pipe(
        expand(dirHandle => {
          if (index > parts.length - 1) return EMPTY;
          return this.recursivelyCreateOrGetFolder(dirHandle, parts[index++], false);
        })
      )
      .pipe(last());
  }

  public recursivelyCreateOrGetFolder(
    parentDirHandle: any,
    folderName: string,
    attemptUnique: boolean
  ): Observable<any> {
    let attempt = 0;
    let attemptFolderName = folderName;
    return defer(() => parentDirHandle.getDirectoryHandle(folderName, { create: false }))
      .pipe(
        expand(dirHandle => {
          if (!attemptUnique) return EMPTY;
          attempt++;
          attemptFolderName = `${folderName}-${attempt}`;
          return defer(() => parentDirHandle.getDirectoryHandle(attemptFolderName, { create: false }));
        }),
        catchError(err => {
          return defer(() => parentDirHandle.getDirectoryHandle(attemptFolderName, { create: true }));
        })
      )
      .pipe(last());
  }
}
